import {
  createContextMutationPrecondition,
  type RelationshipStatus,
  type OrganizationMemberIdentityResponse,
} from "@kontextwerk/control-web-client"
import { buildPermissionState } from "@kontextwerk/control-web-frame/shared/logic/authorization"
import { rejectControlContext } from "@kontextwerk/control-web-frame/shared/logic/authentication"
import { getControlWebClient } from "../../client"
import type { CapturedMembershipSettings } from "../../membership-settings"
import { createIdentityAdministrationApi } from "../../identity-administration"
import { KEHRBLATT_CONTEXT_CATALOG } from "../../../../_shared/_logic/context-capabilities"

/** Access facts of one roster membership; role and revision only come from the membership read. */
export type FrameMembershipAccess = Readonly<{
  status: RelationshipStatus
  role_id: string | null
  revision: number | null
  email: string | null
}>

/** Labels are not eligibility. Read status from its owner, retaining only the admitted tenant. */
export const listFrameMembershipStatuses = async (
  binding: CapturedMembershipSettings,
  organizationId: string,
  members: readonly OrganizationMemberIdentityResponse[],
): Promise<ReadonlyMap<string, FrameMembershipAccess>> => {
  const { session } = binding
  const context = session.context
  const current = () => {
    if (!binding.current() || binding.signal.aborted)
      throw new DOMException("Obsolete membership roster", "AbortError")
  }
  current()

  if (context.kind !== "organization" || context.organization_id !== organizationId)
    throw new TypeError("Membership roster scope mismatch")

  if (
    session.password_change_required ||
    (context.delegation && !(Date.parse(context.delegation.expires_at) > Date.now()))
  )
    throw new DOMException("Obsolete membership roster admission", "AbortError")

  const statuses = new Map<string, FrameMembershipAccess>()

  if (members.some((member) => member.organization_id !== organizationId))
    throw new TypeError("Foreign roster identity")

  const labels = new Map(members.map((member) => [member.membership_id.toLowerCase(), member]))

  if (labels.size !== members.length) throw new TypeError("Duplicate roster membership")

  if (!members.length) return statuses

  const permissions = buildPermissionState(session, KEHRBLATT_CONTEXT_CATALOG)

  if (!permissions.allows("core.memberships", "view")) {
    // Current organization admission proves this one membership, not any other label's status.
    const own = labels.get(context.membership_id.toLowerCase())

    if (own?.id === (context.delegation?.represented_user_id ?? session.user.id))
      statuses.set(own.membership_id, {
        status: "active",
        role_id: null,
        revision: null,
        email: null,
      })

    return statuses
  }

  const api = createIdentityAdministrationApi(getControlWebClient(), session, {
    signal: binding.signal,
    isCurrent: binding.current,
    mutationContext: createContextMutationPrecondition({
      expected_session_id: session.session_id,
      expected_context_revision: session.context_revision,
    }),
  })
  const cursors = new Set<string>()
  const seen = new Set<string>()
  let after: string | null = null
  try {
    do {
      current()
      const page = await api.memberships(after)
      current()
      for (const member of page.items) {
        if (seen.has(member.id)) throw new TypeError("Unexpected membership status scope")

        seen.add(member.id)
        const label = labels.get(member.id)

        if (label && label.id.toLowerCase() !== member.user_id)
          throw new TypeError("Membership identity binding mismatch")

        if (label)
          statuses.set(label.membership_id, {
            status: member.status,
            role_id: member.role_id,
            revision: member.revision,
            email: member.email,
          })
      }
      after = page.next_after_id

      if (after && (cursors.has(after) || !page.items.length))
        throw new TypeError("Membership status cursor did not advance")

      if (after) cursors.add(after)
    } while (after && statuses.size < members.length)

    return statuses
  } catch (error) {
    if (
      binding.current() &&
      [401, 403, 409].includes((error as { statusCode?: number })?.statusCode ?? 0)
    )
      rejectControlContext()

    throw error
  }
}
